import type { Tag, StatusId } from './tags';
import type { Modifier } from './stats';

export interface StatusApply {
  id: StatusId;
  /** seconds */
  duration: number;
  stacks?: number;
  /** For damaging statuses: fraction of the applier's power per second per stack. Defaults from STATUS. */
  power?: number;
}

export type ProjectilePattern = 'aimed' | 'nearest' | 'ring' | 'random' | 'forward' | 'spiral';

export interface ProjectileAction {
  type: 'projectile';
  count: number;
  /** total spread in radians when count > 1 (ignored for ring) */
  spread: number;
  pattern: ProjectilePattern;
  speed: number;
  range: number;
  /** multiplier on the caster's power */
  damage: number;
  radius: number;
  pierce?: number;
  bounce?: number;
  /** turn rate in rad/s toward nearest enemy */
  homing?: number;
  tags: Tag[];
  status?: StatusApply[];
  onHit?: Action;
  onExpire?: Action;
  visual: string;
  knockback?: number;
  /** projectiles orbit the caster instead of travelling */
  orbit?: { radius: number; duration: number; angVel: number };
  returning?: boolean;
}

export interface AreaAction {
  type: 'area';
  /** melee = arc in front of the caster limited by range */
  at: 'self' | 'aim' | 'target' | 'nearest' | 'melee';
  radius: number;
  /** melee arc (radians) and reach */
  arc?: number;
  range?: number;
  damage: number;
  tags: Tag[];
  status?: StatusApply[];
  /** seconds the zone persists; 0 = instant */
  duration?: number;
  /** damage applications per second while persisting */
  tickRate?: number;
  visual: string;
  knockback?: number;
  /** pulls enemies toward the centre (px/s) */
  pull?: number;
  /** heals allies inside as a fraction of caster power */
  healAllies?: number;
  /** damage bonus per stack of the given status on the target, consumes stacks */
  consume?: { status: StatusId; perStack: number };
}

export interface SummonAction {
  type: 'summon';
  unit: string;
  count: number;
  duration: number;
  at: 'self' | 'aim';
  /** keep at most `count` of this unit alive: recasting heals and refreshes them instead of stacking */
  unique?: boolean;
}

export interface BuffAction {
  type: 'buff';
  target: 'self' | 'allies' | 'enemies';
  radius?: number;
  status: StatusApply[];
  visual?: string;
  /** removes debuffs from allies / buffs from enemies before applying */
  cleanse?: boolean;
  /** enemies are gathered around the caster instead of the aim point */
  at?: 'self' | 'aim';
}

/** Cycles through mutually exclusive stances (statuses); the next one replaces the active one. */
export interface StanceAction {
  type: 'stance';
  statuses: StatusId[];
  duration: number;
  visual?: string;
}

export interface HealAction {
  type: 'heal';
  target: 'self' | 'allies' | 'lowest';
  radius?: number;
  /** fraction of the target's max hp */
  pct?: number;
  /** multiplier of caster power */
  power?: number;
  visual?: string;
}

export interface DashAction {
  type: 'dash';
  distance: number;
  damage?: number;
  radius?: number;
  tags?: Tag[];
  status?: StatusApply[];
  /** action executed at the start point after dashing */
  trail?: Action;
  visual?: string;
}

export interface ChainAction {
  type: 'chain';
  jumps: number;
  range: number;
  damage: number;
  tags: Tag[];
  status?: StatusApply[];
  visual: string;
}

export interface SelfCostAction {
  type: 'cost';
  /** fraction of current hp removed from the caster (blood magic) */
  hpPct: number;
}

export interface MultiAction {
  type: 'multi';
  actions: Action[];
}

export type Action =
  | ProjectileAction
  | AreaAction
  | SummonAction
  | BuffAction
  | HealAction
  | DashAction
  | ChainAction
  | SelfCostAction
  | StanceAction
  | MultiAction;

export type HookTrigger =
  | 'hit'
  | 'kill'
  | 'damaged'
  | 'dash'
  | 'cast'
  | 'lowhp'
  | 'periodic'
  | 'levelup'
  | 'pickup'
  | 'summonDeath'
  | 'crit';

export interface Hook {
  on: HookTrigger;
  /** only fire when the triggering hit carries one of these tags */
  tags?: Tag[];
  chance?: number;
  /** seconds between activations */
  cooldown?: number;
  /** for 'periodic' */
  period?: number;
  /** for 'lowhp': fraction */
  hpBelow?: number;
  action: Action;
}

export type Flag =
  | 'noHeal'
  | 'noMoveShoot'
  | 'noSummons'
  | 'lowHpDamage'
  | 'allChill'
  | 'killExplode'
  | 'auraAllies'
  | 'necromancer'
  | 'dashResetOnKill'
  | 'lootDouble'
  | 'summonsInheritArmor'
  | 'holySmite'
  | 'blightSpreads'
  | 'shatter'
  | 'consumablesDouble'
  | 'thornsScaleArmor'
  | 'curseAmplify'
  | 'bondRedirect';

export interface Perk {
  mods?: Modifier[];
  hooks?: Hook[];
  flags?: Flag[];
}

/**
 * Shallow overrides an aspect applies on top of the action it inherits (ring two on ring one).
 * Keys present replace the field; `addStatus` appends. For `multi` actions the patch hits every part.
 */
export interface ActionPatch {
  unit?: string;
  count?: number;
  spread?: number;
  duration?: number;
  radius?: number;
  damage?: number;
  range?: number;
  speed?: number;
  pierce?: number;
  homing?: number;
  knockback?: number;
  pull?: number;
  tickRate?: number;
  visual?: string;
  tags?: Tag[];
  status?: StatusApply[];
  addStatus?: StatusApply[];
  healAllies?: number;
  at?: 'self' | 'aim';
  unique?: boolean;
}

export function patchAction(action: Action, patch: ActionPatch): Action {
  if (action.type === 'multi') return { ...action, actions: action.actions.map((a) => patchAction(a, patch)) };
  const { addStatus, ...rest } = patch;
  const out = { ...action } as Action & Record<string, unknown>;
  for (const [k, v] of Object.entries(rest)) if (v !== undefined && k in action) (out as Record<string, unknown>)[k] = v;
  // unit/count/duration only make sense on summons, at on area/buff/summon
  if (patch.unit !== undefined && action.type === 'summon') out.unit = patch.unit;
  if (patch.at !== undefined && (action.type === 'summon' || action.type === 'area' || action.type === 'buff')) (out as Record<string, unknown>).at = patch.at;
  if (addStatus && addStatus.length && (action.type === 'projectile' || action.type === 'area' || action.type === 'buff' || action.type === 'dash' || action.type === 'chain')) {
    (out as Record<string, unknown>).status = [...((action as { status?: StatusApply[] }).status ?? []), ...addStatus];
  }
  return out as Action;
}
